<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { CalendarDate, type DateValue } from '@internationalized/date';
import { useRegDialog } from '@/composables/use-reg-dialog';
import { useProfile } from '@/composables/use-profile';
import { useBodyScrollLock } from '@/composables/use-body-scroll-lock';
import { useExamSites } from '@/composables/use-exam-sites';
import { useMyExams } from '@/composables/use-my-exams';
import { $fetch } from '@/utils/fetch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon, Maximize2, Minimize2 } from '@lucide/vue';
import { cn } from '@/utils/shadcn';

const router = useRouter();
const { state, closeReg, openLock } = useRegDialog();

// 弹窗打开时锁定背景滚动，关闭时自动恢复
useBodyScrollLock(() => state.open);

const flowSteps = ['选择科目', '填写信息', '选择考点', '选日期场次', '确认提交', '线下缴费', '线下考试', '获取证书'];

const isFullscreen = ref(false);

// 报考人信息来自用户中心，弹窗内只读展示；未完善时引导前往填写
const { profile, isComplete } = useProfile();

// 报名成功后作废我的考试缓存，使跳转后列表重新拉取
const { invalidate: invalidateMyExams } = useMyExams();

// 考点与场次来自 /api/web/exam/get（仅报名弹窗使用）
const { sites, loading: sitesLoading, fetchSites, reset: resetSites } = useExamSites();

// 遮罩与弹窗盒 class：全屏时撑满视口
const overlayCls = computed(() => cn(
  'modal-overlay fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 backdrop-blur-sm',
  isFullscreen.value ? 'p-0' : 'p-6 max-sm:p-3',
));

const boxCls = computed(() => cn(
  'modal-box flex flex-col w-full overflow-hidden bg-surface-card border border-primary-light shadow-[0_24px_64px_rgba(0,0,0,0.25)]',
  isFullscreen.value
    ? 'h-full max-w-none max-h-none rounded-none border-0'
    : 'max-w-[720px] max-h-[85vh] rounded-xl max-sm:max-h-[92vh]',
));

const selectedSiteId = ref('');
const selectedDate = ref<DateValue | undefined>(); // 日历选中值（CalendarDate）
const calPlaceholder = ref<DateValue | undefined>(); // 日历展示月份
const selectedTime = ref('');
const agreeNotice = ref(false);
const agreePrivacy = ref(false);
const submitting = ref(false);

const currentSite = computed(() =>
  sites.value.find(s => s.siteId === selectedSiteId.value) ?? null,
);

const siteSlots = computed(() => currentSite.value?.slots ?? []);

// ISO 'YYYY-MM-DD' -> CalendarDate
function toDateValue(iso: string): CalendarDate {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new CalendarDate(y, m, d);
}

// 可预约日期（有场次的日期，去重，保留出场顺序）
const availableDates = computed<DateValue[]>(() => {
  const seen = new Set<string>();
  const list: DateValue[] = [];
  for (const s of siteSlots.value) {
    if (s.date && !seen.has(s.date)) {
      seen.add(s.date);
      list.push(toDateValue(s.date));
    }
  }
  return list;
});

const availableDateKeys = computed(() => new Set(availableDates.value.map(d => d.toString())));

// 弹窗打开时刻，用于判断是否已过报名截止时间（避免长时间挂页后沿用初次渲染的时间）
const openedAt = ref(new Date());

// 报名截止时间为考试日前一天 14:00：14:00 前最早可约明天，14:00 后最早可约后天
const minSelectableDate = computed<CalendarDate>(() => {
  const now = openedAt.value;
  const today = new CalendarDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
  return now.getHours() < 14 ? today.add({ days: 1 }) : today.add({ days: 2 });
});

// 可选日期 = 有场次且不早于最早可约日期
const selectableDates = computed<DateValue[]>(() =>
  availableDates.value.filter(d => d.compare(minSelectableDate.value) >= 0),
);

// 日历中仅放行可约日期：须有场次且不早于最早可约日期，其余置灰不可选
function isDateUnavailable(date: DateValue): boolean {
  if (!availableDateKeys.value.has(date.toString())) {
    return true;
  }
  return date.compare(minSelectableDate.value) < 0;
}

const selectedDateIso = computed(() => selectedDate.value?.toString() ?? '');

// 当前日期下可选的时间段
const availableTimes = computed(() =>
  siteSlots.value.find(s => s.date === selectedDateIso.value)?.times ?? [],
);

// 日历触发器展示文案，形如「8月19日」
const dateTriggerLabel = computed(() => {
  if (!selectedDate.value) { return ''; }
  return `${selectedDate.value.month}月${selectedDate.value.day}日`;
});

// "HH:mm:ss-HH:mm:ss" -> "HH:mm–HH:mm"
function formatTimeRange(t: string): string {
  const [a, b] = t.split('-');

  return `${a.slice(0, 5)}–${b.slice(0, 5)}`;
}

// 弹窗打开时拉取考点场次并重置选中；关闭时清空缓存
watch(() => state.open, (open) => {
  if (open) {
    openedAt.value = new Date();
    isFullscreen.value = false;
    selectedSiteId.value = '';
    selectedDate.value = undefined;
    calPlaceholder.value = undefined;
    selectedTime.value = '';
    agreeNotice.value = false;
    agreePrivacy.value = false;
    submitting.value = false;

    if (state.subject?.id) {
      fetchSites(state.subject.id);
    }
  }
  else {
    resetSites();
  }
});

// 切换考点：重置日期/场次，并把日历跳到首个可约日期所在月份
watch(selectedSiteId, () => {
  selectedDate.value = undefined;
  selectedTime.value = '';
  // 日历跳到首个可约日期所在月份，避免停在全是置灰的月份
  calPlaceholder.value = selectableDates.value[0];
});

watch(selectedDate, () => {
  selectedTime.value = '';
});

const subject = computed(() => state.subject);

const canSubmit = computed(() =>
  subject.value
  && isComplete.value
  && selectedSiteId.value !== ''
  && selectedTime.value !== ''
  && agreeNotice.value
  && agreePrivacy.value,
);

const feeText = computed(() => subject.value?.fee ?? '—');

async function submitReg() {
  if (!canSubmit.value || submitting.value) {
    return;
  }

  // time 原样回传 get 下发的 "HH:mm:ss-HH:mm:ss"
  submitting.value = true;
  const { isSuccess } = await $fetch('/api/web/register', {
    data: {
      exam_id: Number(subject.value?.id),
      site_id: Number(selectedSiteId.value),
      date: selectedDateIso.value,
      time: selectedTime.value,
    },
  });
  submitting.value = false;

  if (!isSuccess) {
    return;
  }

  // 作废我的考试缓存，确保跳转后列表展示本次新报名
  invalidateMyExams();
  openLock();
}

function close() {
  closeReg();
}

function gotoNotice() {
  // 新标签页打开，弹窗保持打开，已填内容不丢失
  window.open('/notice', '_blank');
}

function gotoPrivacy() {
  window.open('/privacy', '_blank');
}

function gotoProfile() {
  close();
  router.push('/user');
}
</script>

<template>
  <div v-if="state.open" :class="overlayCls">
    <div :class="boxCls">
      <div class="shrink-0 flex items-center justify-between p-5 bg-surface-card border-b border-border-subtle max-sm:p-4">
        <h3 class="text-text-primary text-lg font-bold">
          考试报名
        </h3>
        <div class="flex items-center gap-1">
          <button
            class="flex items-center justify-center size-8 rounded-full bg-muted text-text-tertiary transition-all hover:bg-primary-subtle hover:text-primary"
            :aria-label="isFullscreen ? '退出全屏' : '全屏'"
            @click="isFullscreen = !isFullscreen"
          >
            <Minimize2 v-if="isFullscreen" class="size-4" />
            <Maximize2 v-else class="size-4" />
          </button>
          <button
            class="flex items-center justify-center size-8 rounded-full bg-muted text-text-tertiary text-lg transition-all hover:bg-primary-subtle hover:text-primary"
            aria-label="关闭"
            @click="close"
          >
            ×
          </button>
        </div>
      </div>

      <div class="custom-scrollbar flex-1 overflow-y-auto">
        <div class="p-6 max-sm:p-4">
          <!-- 流程 -->
          <div class="flex flex-wrap gap-2 mb-5 pb-5 border-b border-border-subtle">
            <template v-for="(s, i) in flowSteps" :key="s">
              <span class="py-1.5 px-3.5 text-primary text-xs font-semibold bg-primary-light rounded-[20px]">{{ s }}</span>
              <span v-if="i < flowSteps.length - 1" class="self-center text-primary-light text-xs">→</span>
            </template>
          </div>

          <!-- 报考科目信息 -->
          <div v-if="subject" class="grid grid-cols-2 gap-4 mb-5 max-sm:grid-cols-1">
            <div>
              <div class="mb-1 text-text-tertiary text-xs">
                报考科目
              </div>
              <div class="text-text-primary text-sm font-semibold">
                {{ subject.name }}
              </div>
            </div>
            <div>
              <div class="mb-1 text-text-tertiary text-xs">
                考试时长
              </div>
              <div class="text-text-primary text-sm font-semibold">
                {{ subject.duration }}
              </div>
            </div>
            <div>
              <div class="mb-1 text-text-tertiary text-xs">
                考试形式
              </div>
              <div class="text-text-primary text-sm font-semibold">
                {{ subject.form }}
              </div>
            </div>
          </div>

          <!-- 报考人信息（只读，来自用户中心；未完善时引导前往填写） -->
          <div class="mt-6 mb-4 text-text-primary text-sm font-bold">
            报考人信息
          </div>
          <div v-if="!isComplete" class="p-5 bg-primary-subtle border border-primary-light rounded-lg">
            <div class="flex items-center gap-3">
              <span class="flex shrink-0 items-center justify-center size-6 text-primary text-[13px] bg-primary-light rounded-md">!</span>
              <p class="flex-1 text-text-secondary text-[13px] leading-relaxed">
                请先完善个人信息后再进行报名。
              </p>
            </div>
            <button
              class="mt-3 ml-9 py-2 px-5 text-white text-[13px] font-semibold bg-primary border-none rounded-lg cursor-pointer transition hover:bg-primary-hover"
              @click="gotoProfile"
            >
              前往完善
            </button>
          </div>
          <div v-else class="p-4 bg-surface-bg border border-border-subtle rounded-lg">
            <div class="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
              <div>
                <div class="mb-1 text-text-tertiary text-xs">
                  姓名
                </div>
                <div class="text-text-primary text-sm font-semibold">
                  {{ profile.name }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-text-tertiary text-xs">
                  证件类型
                </div>
                <div class="text-text-primary text-sm font-semibold">
                  {{ profile.idType }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-text-tertiary text-xs">
                  证件号码
                </div>
                <div class="text-text-primary text-sm font-semibold">
                  {{ profile.idNo }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-text-tertiary text-xs">
                  身份类型
                </div>
                <div class="text-text-primary text-sm font-semibold">
                  {{ profile.role || '未填写' }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-text-tertiary text-xs">
                  工作单位
                </div>
                <div class="text-text-primary text-sm font-semibold">
                  {{ profile.org || '未填写' }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-text-tertiary text-xs">
                  毕业院校
                </div>
                <div class="text-text-primary text-sm font-semibold">
                  {{ profile.school || '未填写' }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-text-tertiary text-xs">
                  手机号码
                </div>
                <div class="text-text-primary text-sm font-semibold">
                  {{ profile.phone }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-text-tertiary text-xs">
                  电子邮箱
                </div>
                <div class="text-text-primary text-sm font-semibold">
                  {{ profile.email }}
                </div>
              </div>
            </div>
            <div class="mt-3 pt-3 border-t border-dashed border-border-subtle">
              <button
                class="text-primary text-[13px] font-semibold bg-transparent border-none cursor-pointer hover:underline"
                @click="gotoProfile"
              >
                修改个人信息
              </button>
            </div>
          </div>
          <!-- 考点与场次 -->
          <div class="mt-6 mb-4 text-text-primary text-sm font-bold">
            考点与场次
          </div>
          <div class="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
            <div>
              <label class="block mb-1.5 text-text-secondary text-[13px] font-semibold">考试考点 <span class="text-destructive">*</span></label>
              <Select v-model="selectedSiteId" :disabled="sitesLoading">
                <SelectTrigger>
                  <SelectValue :placeholder="sitesLoading ? '考点加载中…' : '请选择考点'" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="site in sites" :key="site.siteId" :value="site.siteId">
                    {{ site.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label class="block mb-1.5 text-text-secondary text-[13px] font-semibold">考试日期 <span class="text-destructive">*</span></label>
              <Popover>
                <PopoverTrigger as-child>
                  <button
                    type="button"
                    :disabled="!currentSite"
                    :class="[
                      'flex items-center justify-between w-full px-3.5 py-2.5 text-sm bg-surface-card border border-border-default rounded-lg outline-none transition cursor-pointer',
                      'focus:border-primary focus:ring-2 focus:ring-primary/10',
                      'disabled:opacity-50 disabled:pointer-events-none',
                      dateTriggerLabel ? 'text-text-primary' : 'text-text-tertiary',
                    ]"
                  >
                    <span>{{ dateTriggerLabel || '请选择日期' }}</span>
                    <CalendarIcon class="size-4 text-text-tertiary" />
                  </button>
                </PopoverTrigger>
                <PopoverContent class="z-[2100] w-auto p-0">
                  <Calendar
                    v-model="selectedDate"
                    v-model:placeholder="calPlaceholder"
                    :is-date-unavailable="isDateUnavailable"
                    :disabled="!currentSite"
                  />
                </PopoverContent>
              </Popover>
              <p class="mt-1.5 text-text-tertiary text-xs">
                报名截止至考试日前一天 14:00
              </p>
            </div>
          </div>
          <div class="mt-4">
            <label class="block mb-1.5 text-text-secondary text-[13px] font-semibold">考试时间 <span class="text-destructive">*</span></label>
            <Select v-model="selectedTime" :disabled="!selectedDate">
              <SelectTrigger>
                <SelectValue placeholder="请选择时间" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="t in availableTimes" :key="t" :value="t">
                  {{ formatTimeRange(t) }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <!-- 考场地址 -->
          <div v-if="currentSite" class="mt-4 p-4 bg-surface-bg border border-border-subtle rounded-lg">
            <div class="flex items-center gap-2 mb-3 text-text-primary text-[13px] font-bold">
              <span class="text-primary">📍</span>考场地址
            </div>
            <div class="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
              <div>
                <div class="mb-1 text-text-tertiary text-xs">
                  考场地址
                </div>
                <div class="text-text-primary text-sm font-semibold">
                  {{ currentSite.location }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-text-tertiary text-xs">
                  交通信息
                </div>
                <div class="text-text-secondary text-sm">
                  {{ currentSite.traffic_info }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-text-tertiary text-xs">
                  联系电话
                </div>
                <div class="text-text-secondary text-sm">
                  {{ currentSite.contact_info }}
                </div>
              </div>
            </div>
          </div>

          <!-- 费用摘要 -->
          <div class="my-5 p-4 bg-primary-light border border-primary-light rounded-lg">
            <div class="flex justify-between text-base font-bold">
              <span>考试费用（现场线下缴费）</span><span>{{ feeText }}</span>
            </div>
          </div>

          <!-- 报考须知 / 隐私同意 -->
          <div class="mb-5 p-4 bg-surface-card border border-border-subtle rounded-lg">
            <div class="flex items-center gap-2 mb-2.5 text-text-primary text-sm font-bold">
              <span class="flex shrink-0 items-center justify-center size-6 text-primary text-[13px] bg-primary-light rounded-md">!</span>
              报名须知与隐私声明
            </div>
            <div class="flex flex-col gap-2.5 mt-3.5 pt-3 border-t border-dashed border-border-subtle">
              <label class="flex items-start gap-2 text-text-secondary text-[13px] leading-relaxed cursor-pointer">
                <input v-model="agreeNotice" type="checkbox" class="shrink-0 size-4 mt-0.5 accent-[var(--color-primary)] cursor-pointer">
                <span>我已阅读并同意<a class="text-primary font-semibold hover:underline" @click="gotoNotice">《考试报名须知》</a>，确认所填信息真实有效。</span>
              </label>
              <label class="flex items-start gap-2 text-text-secondary text-[13px] leading-relaxed cursor-pointer">
                <input v-model="agreePrivacy" type="checkbox" class="shrink-0 size-4 mt-0.5 accent-[var(--color-primary)] cursor-pointer">
                <span>我已阅读并同意<a class="text-primary font-semibold hover:underline" @click="gotoPrivacy">《隐私政策》</a>，授权使用个人信息完成本次报名。</span>
              </label>
            </div>
          </div>

          <div class="flex gap-3 justify-end mt-6 pt-5 border-t border-border-subtle">
            <button class="py-2.5 px-6 text-text-secondary font-semibold text-sm bg-muted border-none rounded-lg cursor-pointer transition hover:opacity-80" @click="close">
              取消
            </button>
            <button
              class="py-2.5 px-6 text-white font-semibold text-sm bg-primary border-none rounded-lg cursor-pointer transition hover:bg-primary-hover disabled:bg-border-default disabled:cursor-not-allowed"
              :disabled="!canSubmit || submitting"
              @click="submitReg"
            >
              {{ submitting ? '提交中…' : '确认提交' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-box {
  animation: cl-modal-in 0.3s ease;
}

@keyframes cl-modal-in {
  from {
    transform: scale(0.96) translateY(10px);
    opacity: 0;
  }

  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}
</style>
